import { Info } from 'lucide-react';
import { useFieldArray, UseFormReturn } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';
import { Button, IconButton } from '@/Domain/components/Shared/Button/Button';
import {
  FormControl,
  FormField,
  FormItem,
  FormMessage,
} from '@/Domain/components/Shared/Form/Form';
import { Input } from '@/Domain/components/Shared/Input/Input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/Domain/components/Shared/Tooltip/Tooltip';
import AddIcon from '@/Domain/components/icons/AddIcon';
import { TrashIcon } from '@/Domain/components/icons/Trash.icon';
import { HttpActionForm } from '@/Domain/hooks/Action/ApiAction/useHttpRequestActionFormV2.hook';

type Props = {
  form: UseFormReturn<HttpActionForm>;
};

/**
 * Editable list of response-value captures. Each row extracts a value from the
 * API response (JSONPath) and saves it to conversation memory under a key,
 * reusable in later steps as {{metadata_<key>}}.
 */
export const MemoryCapture = ({ form }: Props) => {
  const { t } = useTranslation('actions_page');

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: 'response_metadata_captures',
  });

  const handleAdd = () => append({ id: uuidv4(), json_path: '', metadata_key: '' });

  return (
    <div>
      <section className="relative grid h-16 grid-cols-[1fr_1fr_auto] items-center gap-2 border-b border-border bg-accent px-4 text-sm font-medium text-muted-foreground">
        <span>{t('sheet.form.memory_capture.table.jsonpath')}</span>
        <span className="flex items-center gap-1.5">
          {t('sheet.form.memory_capture.table.memory_key')}
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="size-3.5 cursor-help text-foreground-subtle transition-colors hover:text-muted-foreground" />
              </TooltipTrigger>
              <TooltipContent className="max-w-xs">
                <span>
                  {t('sheet.form.memory_capture.key_hint')}{' '}
                  <code className="rounded bg-muted px-1 py-0.5 font-mono text-xs">
                    {'{{metadata_<key>}}'}
                  </code>
                </span>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </span>
        <IconButton size="sm" onClick={handleAdd} data-testid="memory-capture-add-button">
          <AddIcon />
        </IconButton>
      </section>

      {fields.length === 0 ? (
        <div className="flex flex-col gap-3 items-center justify-center mt-7">
          <p className="text-sm font-medium text-foreground">
            {t('sheet.form.memory_capture.empty.title')}
          </p>
          <p className="text-center text-sm text-muted-foreground max-w-md">
            <Trans i18nKey="sheet.form.memory_capture.empty.description" t={t} />
          </p>
          <Button onClick={handleAdd} data-testid="memory-capture-add-empty-button">
            <AddIcon />
            {t('sheet.form.memory_capture.add_button')}
          </Button>
        </div>
      ) : (
        <div className="mt-4 space-y-2">
          {fields.map((field, index) => (
            <div key={field.id} className="grid grid-cols-[1fr_1fr_auto] items-start gap-2 px-2">
              <FormField
                name={`response_metadata_captures.${index}.json_path`}
                render={({ field: inputField, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        data-testid={`memory-capture-jsonpath-${index}`}
                        placeholder={t('sheet.form.memory_capture.jsonpath_placeholder')}
                        error={!!fieldState.error}
                        {...inputField}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <FormField
                name={`response_metadata_captures.${index}.metadata_key`}
                render={({ field: inputField, fieldState }) => (
                  <FormItem>
                    <FormControl>
                      <Input
                        data-testid={`memory-capture-key-${index}`}
                        placeholder={t('sheet.form.memory_capture.key_placeholder')}
                        error={!!fieldState.error}
                        {...inputField}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />
              <IconButton
                size="sm"
                variant="secondary"
                onClick={() => remove(index)}
                data-testid={`memory-capture-remove-${index}`}
                aria-label={t('sheet.form.memory_capture.remove_aria')}
              >
                <TrashIcon />
              </IconButton>
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
